"use client";

import { MetronomeDivisionGlyph } from "@/components/ui/atoms/metronome-division-glyph";
import type { DivisionId } from "@/lib/studio/studio";

type DivisionOption = {
  id: DivisionId;
  label: string;
};

const DIVISION_OPTIONS: readonly DivisionOption[] = [
  { id: "quarter", label: "Noires" },
  { id: "eighths", label: "Croches" },
  { id: "triplet", label: "Triolet" },
  { id: "sixteenth", label: "Doubles" },
  { id: "dotted", label: "Pointée" },
  { id: "swing", label: "Swing" },
];

interface MetronomeDivisionPickerProps {
  value: DivisionId;
  onChange: (id: DivisionId) => void;
}

/**
 * Horizontal row of division cards. Scrollable by wheel/trackpad/touch when
 * the popover width can't fit the full row, the right-edge fade hints at
 * the overflow. The scrollbar is hidden for a cleaner look.
 */
export function MetronomeDivisionPicker({
  value,
  onChange,
}: MetronomeDivisionPickerProps) {
  return (
    <div className="relative">
      <div
        className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        role="radiogroup"
        aria-label="Subdivision du temps"
      >
        {DIVISION_OPTIONS.map((d) => {
          const active = value === d.id;

          return (
            <button
              key={d.id}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(d.id)}
              className={[
                "flex-none min-w-16 px-2.5 py-1.5 rounded-md border transition-colors duration-150 cursor-pointer",
                "flex flex-col items-center gap-0.5",
                active
                  ? "border-[var(--color-violet-glow)] bg-[var(--color-violet-soft)] text-text-primary"
                  : "border-border bg-bg-deep text-text-secondary hover:border-border-subtle",
              ].join(" ")}
              style={
                active
                  ? { boxShadow: "0 0 10px var(--color-violet-glow)" }
                  : undefined
              }
            >
              <MetronomeDivisionGlyph id={d.id} />
              <span className="text-[10px] leading-tight whitespace-nowrap font-medium">
                {d.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Right-edge fade, hints at horizontal overflow. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-6 rounded-r-md"
        style={{
          background:
            "linear-gradient(270deg, var(--color-bg-surface) 0%, transparent 100%)",
        }}
      />
    </div>
  );
}
